import { get } from "lodash";

import ROLES_AND_PERMISSIONS from "@/constants/roles-and-permissions";
import { FEATURES, SUB_FEATURES } from "@/constants/system-feature";

const isLoggedIn = state => {
  const exp = state.exp;
  const accessToken = state.access_token;
  if (exp && accessToken) {
    const tokenExpiration = Number(state.exp);
    const isExpired = tokenExpiration < new Date() / 1000;
    return !isExpired;
  }
  return false;
};

const getMyInfo = state => {
  return state.currentLoggedInInfo;
};

const getMyUserInfo = state => {
  return state.myUserInfo;
};

const getMyPermissions = state => {
  return state.currentUserPermissions;
};

const getMyUserId = state => {
  const { sim } = state.currentLoggedInInfo;
  return sim.user.subject || null;
};

const getMyFirstName = state => {
  return get(state.currentLoggedInInfo.sim.user, "firstName", "");
};

const getMyRolesAndPermissions = state => {
  return get(state, "userRolesAndPermission", []);
};

const getFeatureAccess = state => {
  const { isSimpatraAdmin } = state.currentUserPermissions;
  const { sections, permissions } = ROLES_AND_PERMISSIONS.ROLES;
  const { FULL, READ, UNAUTHORIZED } = ROLES_AND_PERMISSIONS.ROLE_ACCESS_TYPE;
  const { EDITABLE } = ROLES_AND_PERMISSIONS.ACCESS_LEVELS;

  const userRoles = state.userRolesAndPermission || [];
  const internalUserRoles = userRoles.filter(
    userRole => userRole.section?.name === sections.INTERNAL_USER.name,
  );

  // Prior logic: allow editable when Simpatra admin or when not applying
  // stricter logic, otherwise require full. New logic: still honor CS selection
  // types (full/edit/read) when present.
  const getCustomerSupportRole = () =>
    internalUserRoles.find(
      role =>
        role.name?.includes(permissions.CUSTOMER_SUPPORT.name) ||
        (role.permissions || []).some(
          permission => permission.name === permissions.CUSTOMER_SUPPORT.name,
        ),
    );

  const customerSupport = getCustomerSupportRole();
  const selectionType = customerSupport?.selectionType;

  /**
   * Resolve access for specific feature based on Customer Support role
   * @param {string} feature - The feature to check access for
   * @returns {string} - Access type (EDITABLE, READ, UNAUTHORIZED)
   *
   * New Access Logic:
   * - Full: Can manage API, Roles, and Organization Management
   * - Edit: Can VIEW API & Roles, but can EDIT Organization Management only
   * - Read: Can ONLY VIEW Organization Management (no API/Roles access)
   */
  const resolveAccessForFeature = feature => {
    if (isSimpatraAdmin) return EDITABLE;
    if (!customerSupport) return UNAUTHORIZED;

    // Full access: all features are editable
    if (selectionType === FULL || selectionType === "full") {
      return EDITABLE;
    }

    // Edit access: different permissions per feature
    if (selectionType === "edit") {
      if (feature === FEATURES.ORGANIZATION_MANAGEMENT) {
        return EDITABLE; // Can edit Organization Management
      }
      if (feature === FEATURES.API_ACCESS || feature === FEATURES.ROLES_AND_PERMISSIONS) {
        return READ; // Can only view API and Roles
      }
      return UNAUTHORIZED;
    }

    // Read access: only Organization Management viewable
    if (selectionType === READ || selectionType === "read") {
      if (feature === FEATURES.ORGANIZATION_MANAGEMENT) {
        return READ; // Can only view Organization Management
      }
      // No access to API and Roles for Read users
      return UNAUTHORIZED;
    }

    return UNAUTHORIZED;
  };

  return [
    {
      feature: FEATURES.API_ACCESS,
      accessType: resolveAccessForFeature(FEATURES.API_ACCESS),
    },
    {
      feature: FEATURES.ROLES_AND_PERMISSIONS,
      accessType: resolveAccessForFeature(FEATURES.ROLES_AND_PERMISSIONS),
    },
    {
      feature: FEATURES.ORGANIZATION_MANAGEMENT,
      subFeature: [
        {
          name: SUB_FEATURES.ORGANIZATION_MANAGEMENT.ORGANIZATION,
          accessType: resolveAccessForFeature(FEATURES.ORGANIZATION_MANAGEMENT),
        },
        {
          name: SUB_FEATURES.ORGANIZATION_MANAGEMENT.VENDOR_RELATIONSHIP,
          accessType: resolveAccessForFeature(FEATURES.ORGANIZATION_MANAGEMENT),
        },
      ],
    },
  ];
};

const getUserLicenses = state => {
  return get(state, "userLicenses", {});
};

export default {
  isLoggedIn,
  getMyInfo,
  getMyUserId,
  getMyFirstName,
  getMyPermissions,
  getMyUserInfo,
  getMyRolesAndPermissions,
  getFeatureAccess,
  getUserLicenses,
};
